import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal, Trash2 } from 'lucide-react';
import {
  REDSHIFT_TARGET_LABELS,
  redshiftExportTarget,
  type RedshiftExportJob,
  type RedshiftExportPhase,
  type RedshiftExportStatus,
} from '@atlas/shared';
import { formatBytes, formatRows, PAGE_SIZE } from '../lib/redshiftExports.js';
import { deleteRedshiftExport } from '../lib/callables.js';
import { useAuth } from '../auth/AuthProvider.js';
import { RedshiftExportDetailModal } from './RedshiftExportDetailModal.js';

interface RedshiftExportsTableProps {
  exports: Array<RedshiftExportJob & { id: string }>;
  loading?: boolean;
  emptyMessage?: string;
}

const PHASE_LABELS: Record<RedshiftExportPhase, string> = {
  connecting: 'Connecting',
  preflight: 'Pre-flight',
  unloading: 'Unloading',
  finalising: 'Finalising',
};

const STATUS_STYLES: Record<RedshiftExportStatus, string> = {
  queued: 'bg-[#f1f1ef] text-[#73726e]',
  running: 'bg-[#e7f0fb] text-[#2383e2]',
  complete: 'bg-[#e5f5f2] text-[#0f7b6c]',
  failed: 'bg-[#fbe8e8] text-[#e03e3e]',
  cancelled: 'bg-[#f1f1ef] text-[#aeaaa4]',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function StatusCell({ job }: { job: RedshiftExportJob }) {
  const label =
    job.status === 'running' && job.phase ? PHASE_LABELS[job.phase] : job.status;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLES[job.status]}`}
    >
      {job.status === 'running' && (
        <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
      )}
      {label}
    </span>
  );
}

/** "12 / 40" while unloading, so progress is visible without opening the run. */
function progressLabel(job: RedshiftExportJob): string {
  const done = job.tableResults?.length ?? 0;
  const planned = job.plan?.length ?? null;
  if (job.status === 'complete') return String(job.totals?.tables ?? done);
  if (planned === null) return done > 0 ? String(done) : '—';
  return `${done} / ${planned}`;
}

function DeleteModal({
  job,
  onClose,
}: {
  job: RedshiftExportJob & { id: string };
  onClose: () => void;
}) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleDelete() {
    setDeleting(true);
    setError(null);
    try {
      await deleteRedshiftExport({ exportId: job.id });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed.');
      setDeleting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-[400px] max-w-[95vw] p-5 flex flex-col gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h2 className="text-sm font-semibold text-[#37352f]">Delete run</h2>
          <p className="text-xs text-[#73726e] mt-1">
            Removes the run for{' '}
            <span className="font-mono text-[#37352f]">{job.schema}</span> from the
            history. The CSVs already written to{' '}
            <span className="font-mono text-[#37352f]">
              s3://{job.s3Bucket}/{job.s3Prefix}
            </span>{' '}
            are left untouched.
          </p>
        </div>
        {error && <p className="text-xs text-[#e03e3e]">{error}</p>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-[#73726e] hover:text-[#37352f]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-[#e03e3e] rounded hover:bg-[#c93232] disabled:opacity-50 transition-colors"
          >
            <Trash2 size={13} />
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RowMenu({ canDelete, onDelete }: { canDelete: boolean; onDelete: () => void }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      const target = e.target as Node;
      if (btnRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  function handleOpen(e: React.MouseEvent) {
    e.stopPropagation();
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }
    setOpen((v) => !v);
  }

  if (!canDelete) return null;

  return (
    <>
      <button
        ref={btnRef}
        onClick={handleOpen}
        className="p-1 rounded text-[#aeaaa4] hover:text-[#37352f] hover:bg-[#f1f1ef] transition-colors"
      >
        <MoreHorizontal size={14} />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            style={{ position: 'fixed', top: pos.top, right: pos.right, zIndex: 9999 }}
            className="bg-white border border-[#e9e9e7] rounded-lg shadow-lg w-36 py-1"
          >
            <button
              onClick={() => {
                setOpen(false);
                onDelete();
              }}
              className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-[#e03e3e] hover:bg-[#fbe8e8] transition-colors"
            >
              <Trash2 size={12} />
              Delete
            </button>
          </div>,
          document.body,
        )}
    </>
  );
}

export function RedshiftExportsTable({
  exports,
  loading = false,
  emptyMessage = 'No Redshift exports yet.',
}: RedshiftExportsTableProps) {
  const { user, userRecord } = useAuth();
  const [detail, setDetail] = useState<(RedshiftExportJob & { id: string }) | null>(
    null,
  );
  const [deleteTarget, setDeleteTarget] = useState<
    (RedshiftExportJob & { id: string }) | null
  >(null);
  const [search, setSearch] = useState('');

  if (loading) {
    return <div className="py-12 text-center text-sm text-[#aeaaa4]">Loading…</div>;
  }
  if (exports.length === 0) {
    return (
      <div className="py-12 text-center">
        <p className="text-sm text-[#aeaaa4]">{emptyMessage}</p>
      </div>
    );
  }

  // Keep the open modal in step with live document updates. Resolved against the
  // full list so an open run keeps updating even once the filter excludes it.
  const openDetail = detail
    ? (exports.find((e) => e.id === detail.id) ?? detail)
    : null;

  const term = search.trim().toLowerCase();
  const visible = term
    ? exports.filter((job) =>
        `${job.schema} ${job.s3Bucket}/${job.s3Prefix}`.toLowerCase().includes(term),
      )
    : exports;
  // The subscription only loads the newest PAGE_SIZE runs, so at the cap a search
  // can miss older matches. Say so rather than implying none exist.
  const atCap = exports.length >= PAGE_SIZE;

  return (
    <>
      {openDetail && (
        <RedshiftExportDetailModal job={openDetail} onClose={() => setDetail(null)} />
      )}
      {deleteTarget && (
        <DeleteModal job={deleteTarget} onClose={() => setDeleteTarget(null)} />
      )}
      <div className="flex items-center justify-between gap-3 mb-3">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter by schema or destination…"
          className="w-72 px-2.5 py-1.5 text-sm border border-[#e9e9e7] rounded focus:outline-none focus:ring-1 focus:ring-[#2383e2] text-[#37352f] placeholder:text-[#aeaaa4]"
        />
        <span className="text-xs text-[#aeaaa4] whitespace-nowrap">
          {term && `${visible.length} of ${exports.length}`}
          {term && atCap && ' · '}
          {atCap && `newest ${PAGE_SIZE} runs only`}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#e9e9e7]">
              {['Schema', 'Redshift', 'Destination', 'Started by', 'Started', 'Status', 'Tables', 'Rows', 'Size'].map(
                (h, i) => (
                  <th
                    key={i}
                    className="py-2 px-3 text-left text-xs font-medium text-[#aeaaa4] whitespace-nowrap"
                  >
                    {h}
                  </th>
                ),
              )}
              <th className="py-2 px-2" />
            </tr>
          </thead>
          <tbody>
            {visible.map((job) => (
              <tr
                key={job.id}
                onClick={() => setDetail(job)}
                className="border-b border-[#f1f1ef] hover:bg-[#fafafa] transition-colors cursor-pointer"
              >
                <td className="py-2.5 px-3 font-medium text-[#37352f] whitespace-nowrap">
                  <span className="font-mono">{job.schema}</span>
                  {job.dryRun && (
                    <span className="ml-2 px-1.5 py-0.5 rounded bg-[#f1f1ef] text-[10px] font-medium text-[#73726e] align-middle">
                      pre-flight
                    </span>
                  )}
                </td>
                <td className="py-2.5 px-3 text-[#73726e] whitespace-nowrap text-xs">
                  {REDSHIFT_TARGET_LABELS[redshiftExportTarget(job)]}
                </td>
                <td
                  className="py-2.5 px-3 text-[#aeaaa4] font-mono text-xs whitespace-nowrap max-w-[220px] truncate"
                  title={`s3://${job.s3Bucket}/${job.s3Prefix}`}
                >
                  {job.s3Bucket}/{job.s3Prefix}
                </td>
                <td className="py-2.5 px-3 text-[#73726e] whitespace-nowrap">
                  {job.createdBy.displayName}
                </td>
                <td className="py-2.5 px-3 text-[#73726e] whitespace-nowrap text-xs">
                  {formatDate(job.createdAt)}
                </td>
                <td className="py-2.5 px-3 whitespace-nowrap">
                  <StatusCell job={job} />
                </td>
                <td className="py-2.5 px-3 text-[#73726e] tabular-nums whitespace-nowrap">
                  {progressLabel(job)}
                </td>
                <td className="py-2.5 px-3 text-[#73726e] tabular-nums whitespace-nowrap">
                  {formatRows(job.totals?.rows ?? null)}
                </td>
                <td className="py-2.5 px-3 text-[#73726e] tabular-nums whitespace-nowrap">
                  {job.totals ? formatBytes(job.totals.bytes) : '—'}
                </td>
                <td className="py-2.5 px-2" onClick={(e) => e.stopPropagation()}>
                  <RowMenu
                    canDelete={
                      (!!userRecord?.isAdmin || job.createdBy.uid === user?.uid) &&
                      job.status !== 'running' &&
                      job.status !== 'queued'
                    }
                    onDelete={() => setDeleteTarget(job)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {visible.length === 0 && (
        <div className="py-12 text-center">
          <p className="text-sm text-[#aeaaa4]">
            No runs match{' '}
            <span className="font-mono text-[#73726e]">{search.trim()}</span>
            {atCap && ' in the runs loaded so far'}.
          </p>
        </div>
      )}
    </>
  );
}
